import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { useState,useEffect } from 'react'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { keepPreviousData,useQuery,useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api,type MaybeEnveloped } from '@/lib/api-client'

import { Search,X,RefreshCw } from 'lucide-react'
import { useOrganizationShop } from '@/hooks/use-organization-shop'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { InventoryEmptyState } from '@/components/dashboard/inventory-empty-state'
import { ReconciliationSidePanel } from '@/features/inventory/reconciliation-side-panel'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { formatInStoreTz } from '@/lib/time'

interface ReconciliationRecord {
  id: string
  packageLabel?: string
  itemName?: string
  localQuantity?: number | string
  metrcQuantity?: number | string
  delta?: number | string
  status?: string
  detectedAt?: string
  createdAt?: string
  rawData?: Record<string, unknown>
}

export const Route = createFileRoute('/_dashboard/inventory/reconciliation')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: z.object({
    page: z.number().catch(1),
    limit: z.number().catch(20),
    q: z.string().catch(''),
    status: z.enum(['DISCREPANCY', 'AUTO_CORRECTED', 'RESOLVED', 'ALL']).catch('DISCREPANCY'),
  }),
  component: ReconciliationLayout,
})

let suppressedReconciliationAutoOpenSignature: string | null = null

function ReconciliationLayout() {
  return <Outlet />
}

function getStatusBadge(status: string | undefined) {
  switch (status) {
    case 'DISCREPANCY':
      return (
        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5 text-amber-700 dark:text-amber-400">
          <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          Open
        </Badge>
      )
    case 'AUTO_CORRECTED':
      return (
        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Auto
        </Badge>
      )
    case 'RESOLVED':
      return (
        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
          Resolved
        </Badge>
      )
    default:
      return (
        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal gap-1.5">
          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />
          Unknown
        </Badge>
      )
  }
}

export function ReconciliationPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, q, status } = useSearch({ from: Route.id })
  const { shopId, isLoading: shopLoading } = useOrganizationShop()
  const queryClient = useQueryClient()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${q}:${status}`

  const [localSearchQuery, setLocalSearchQuery] = useState(q)
  const debouncedSearch = useDebouncedValue(localSearchQuery, 500)
  const [isRefreshing, setIsRefreshing] = useState(false)
  void queryClient

  const reconciliationQuery = useQuery({
    queryKey: queryKeys.reconciliations(shopId!, { page, limit, search: debouncedSearch, status }),
    queryFn: async () => {
      const params: Record<string, string | number> = { shopId: shopId!, page, limit }
      if (debouncedSearch) params.search = debouncedSearch
      if (status !== 'ALL') params.status = status
      const response = await api.get<ReconciliationRecord[] & { data?: ReconciliationRecord[]; meta?: { total?: number; totalPages?: number } }>(`/metrc/sync/reconciliation`, { params })
      // The intersection type makes the non-array branch `never`; cast keeps the legacy fallback compiling unchanged.
      const data = Array.isArray(response) ? response : ((response as { data?: ReconciliationRecord[] }).data || [])
      const meta = response.meta
      return {
        data: data as ReconciliationRecord[],
        meta: {
          total: meta?.total || data.length,
          totalPages: meta?.totalPages || 1,
        },
      }
    },
    enabled: !!shopId && !shopLoading,
    // Switching filters/pages keeps the current rows on screen instead of
    // flashing a skeleton, and survives this route's panel-open remount.
    placeholderData: keepPreviousData,
  })

  const summaryQuery = useQuery({
    queryKey: queryKeys.reconciliationSummary(shopId),
    queryFn: async () => {
      const response = await api.get<MaybeEnveloped<Record<string, number>>>(`/metrc/sync/reconciliation/summary`, { params: { shopId } })
      return (response?.data || response || {}) as Record<string, number>
    },
    enabled: !!shopId && !shopLoading,
    // The counts don't change while the user clicks between filters — don't
    // refetch them on every remount.
    staleTime: 60_000,
  })

  const records = reconciliationQuery.data?.data ?? []
  const loading = reconciliationQuery.isLoading
  // With keepPreviousData, `records` still holds the PREVIOUS filter's rows
  // while the new ones load. Auto-opening off those would navigate to a record
  // that's about to disappear — and because $item is a sibling route, that
  // navigation REMOUNTS this page and re-runs every query. Wait for the fetch
  // to land so each filter switch causes exactly one navigation.
  const isFetching = reconciliationQuery.isFetching
  const summaryCounts = summaryQuery.data ?? {}
  const totalItems = reconciliationQuery.data?.meta.total ?? 0
  const totalPages = reconciliationQuery.data?.meta.totalPages ?? 1
  const currentPageHasSelectedRecord = !!selectedId && records.some((record) => record.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedRecord) return selectedId
    if (loading || isFetching || records.length === 0) return undefined
    if (!selectedId && suppressedReconciliationAutoOpenSignature === pageSignature) return undefined
    return records[0].id
  })()

  // Only for a deep-link: an id in the URL that isn't on the current page.
  // It must NOT fire mid-filter-switch — the old selection is legitimately
  // absent from the new list, and asking the server for it 404s, retries, and
  // reports to Sentry on every single filter click. Wait for the fetch to
  // settle; by then the auto-open has already picked a valid record.
  const directRecordQuery = useQuery({
    queryKey: queryKeys.reconciliationRecord(selectedId),
    queryFn: async () => {
      const res = await api.get<MaybeEnveloped<ReconciliationRecord>>(`/metrc/sync/reconciliation/${selectedId}`)
      return (res?.data || res) as ReconciliationRecord
    },
    enabled: !!selectedId && !loading && !isFetching && !records.some((r) => r.id === selectedId),
    retry: false,
  })

  const selectedRecord =
    records.find((r) => r.id === effectiveSelectedId) ??
    (selectedId && effectiveSelectedId === selectedId ? directRecordQuery.data : null) ??
    null

  const setSelectedId = React.useCallback((id: string | null) => {
    if (id) {
      navigate({
        to: '/inventory/reconciliation/$item',
        params: { item: id },
        search: (prev) => ({ ...prev }),
      })
      return
    }

    navigate({ to: '/inventory/reconciliation', search: (prev) => ({ ...prev }) })
  }, [navigate])

  const closeSelectedRecord = React.useCallback(() => {
    suppressedReconciliationAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/inventory/reconciliation')) {
          suppressedReconciliationAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  // Sync the debounced search term into the URL — but ONLY when it actually
  // differs from what's already there. The old version navigated on every mount
  // (and this page REMOUNTS whenever the panel opens, because $item is a sibling
  // route), which fed a navigate → remount → navigate loop: one filter click
  // refetched the same list three times and left the page unresponsive.
  useEffect(() => {
    const next = debouncedSearch || undefined
    if (next === (q || undefined)) return
    navigate({ to: '.', search: (prev) => ({ ...prev, q: next, page: 1 }), replace: true })
  }, [debouncedSearch, q, navigate])

  return (
    <div className="space-y-6 h-full">
      <PageBreadcrumb />

      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Reconciliation</h2>
          <p className="text-muted-foreground mt-1">Review discrepancies between local and Metrc quantities.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative max-w-sm flex-1 group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by label, item..."
            className="pl-9 pr-8"
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
          />
          {localSearchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocalSearchQuery('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 self-start"
          title="Refresh"
          disabled={isRefreshing}
          onClick={() => {
            setIsRefreshing(true)
            void reconciliationQuery.refetch()
            setTimeout(() => setIsRefreshing(false), 600)
          }}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={status === 'DISCREPANCY' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, status: 'DISCREPANCY' as const, page: 1 }) })}
          >
            <span className="inline-flex items-center gap-2">
              Unresolved
              {summaryCounts.DISCREPANCY !== undefined && (
                <Badge variant="secondary" className="text-[10px] h-5 px-1.5 font-normal">
                  {summaryCounts.DISCREPANCY}
                </Badge>
              )}
            </span>
          </Button>
          <Button
            variant={status === 'AUTO_CORRECTED' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, status: 'AUTO_CORRECTED' as const, page: 1 }) })}
          >
            Auto-Corrected
          </Button>
          <Button
            variant={status === 'RESOLVED' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, status: 'RESOLVED' as const, page: 1 }) })}
          >
            Resolved
          </Button>
          <Button
            variant={status === 'ALL' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, status: 'ALL' as const, page: 1 }) })}
          >
            All
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div
          className={cn(
            'flex-1 min-w-0 transition-all duration-300',
            !loading && records.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden',
            selectedRecord ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full',
          )}
        >
          {!loading && records.length === 0 ? (
            <InventoryEmptyState 
              title="No discrepancies found"
              description="All packages match Metrc quantities."
            />
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-14rem)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[140px]">Package Label</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="w-[120px] text-right">Local Qty</TableHead>
                      <TableHead className="w-[120px] text-right">Metrc Qty</TableHead>
                      <TableHead className="w-[90px] text-right">Delta</TableHead>
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[120px]">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading
                      ? Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                            <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                            <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                            <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          </TableRow>
                        ))
                      : records.map((record) => {
                          const rawData = record.rawData ?? {}
                          const label = record.packageLabel ?? (rawData.PackageLabel as string | undefined) ?? '\u2014'
                          const itemName = record.itemName ?? (rawData.ItemName as string | undefined) ?? 'Unknown'
                          const localQty = record.localQuantity ?? (rawData.LocalQuantity as number | undefined) ?? '\u2014'
                          const metrcQty = record.metrcQuantity ?? (rawData.MetrcQuantity as number | undefined) ?? '\u2014'
                          const delta = record.delta ?? (rawData.Delta as number | undefined) ?? '\u2014'
                          const dateValue = record.detectedAt ?? record.createdAt
                          const dateLabel = dateValue ? formatInStoreTz(dateValue, 'MMM dd') : 'N/A'

                          return (
                            <TableRow
                              key={record.id}
                              className={cn(
                                'cursor-pointer hover:bg-muted/50 transition-colors',
                                effectiveSelectedId === record.id && 'bg-muted/80',
                              )}
                              onClick={() => {
                                if (effectiveSelectedId === record.id) {
                                  closeSelectedRecord()
                                  return
                                }

                                setSelectedId(record.id)
                              }}
                            >
                              <TableCell className="font-mono text-xs">{label}</TableCell>
                              <TableCell>
                                <div className="font-medium truncate max-w-[220px]" title={String(itemName)}>
                                  {itemName}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">{String(localQty)}</TableCell>
                              <TableCell className="text-right font-medium">{String(metrcQty)}</TableCell>
                              <TableCell className={cn('text-right font-mono', Number(delta) < 0 ? 'text-red-500' : 'text-emerald-500')}>
                                {String(delta)}
                              </TableCell>
                              <TableCell>{getStatusBadge(record.status)}</TableCell>
                              <TableCell className="text-muted-foreground text-xs font-medium">{dateLabel}</TableCell>
                            </TableRow>
                          )
                        })}
                  </TableBody>
                </Table>
              </div>

              <DataTablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                entityName="reconciliations"
                isLoading={reconciliationQuery.isLoading}
                onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
                onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
              />
            </>
          )}
        </div>

        {selectedRecord && (
          <div className="w-full md:w-[320px] lg:w-[360px] sticky top-0 shrink-0 mt-0 flex flex-col h-fit max-h-[calc(100vh-6rem)]">
            <ReconciliationSidePanel
              open={!!selectedId}
              onOpenChange={(open: boolean) => {
                if (!open) closeSelectedRecord()
              }}
              record={selectedRecord}
            />
          </div>
        )}
      </div>
    </div>
  )
}
